import { useUnit } from 'effector-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signInFx } from '../../entities/session';
import { ApiError } from '../../shared/api/client';
import { cn } from '../../shared/lib/cn';
import { Button, Input } from '../../shared/ui';

/** The owner's sign-in card. Botimir has no sign-up: the owner is created on the server. */
export function SignInCard({ className }: { className?: string }) {
  const { t } = useTranslation();
  const id = useId();
  const [signIn, pending] = useUnit([signInFx, signInFx.pending]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      noValidate
      aria-labelledby={`${id}-title`}
      className={cn('rounded-lg border border-border-subtle bg-bg-1 p-6', className)}
      onSubmit={(event) => {
        event.preventDefault();
        if (!email.trim() || !password) {
          setError(t('signIn.missing'));
          return;
        }
        setError(null);
        signIn({ email, password }).catch((failure: unknown) => {
          setError(failure instanceof ApiError ? failure.message : t('errors.generic'));
        });
      }}
    >
      <h2 id={`${id}-title`} className="text-base font-medium">
        {t('signIn.title')}
      </h2>
      <p className="mt-1 text-sm text-text-3">{t('signIn.body')}</p>
      <div className="mt-5 space-y-3">
        <label className="block space-y-1">
          <span className="text-xs text-text-3">{t('signIn.email')}</span>
          <Input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-text-3">{t('signIn.password')}</span>
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </label>
      </div>
      {error && (
        <p role="alert" className="mt-3 text-xs text-negative">
          {error}
        </p>
      )}
      <Button type="submit" variant="primary" size="lg" className="mt-5 w-full" disabled={pending}>
        {t('signIn.submit')}
      </Button>
    </form>
  );
}
